import type { OutreachDraft } from '@discovr/types';
import { Avatar, AvatarFallback, AvatarImage } from '../../../../components/ui/avatar';
import { avatarInitials } from '../avatarInitials';
import { RichBody } from '../richBody';
import { addressGuardReviewNote } from '../sendGuardCopy';

export interface ReviewMailCardProps {
  draft: OutreachDraft;
  /** The effective recipient (override, else primary); undefined when the creator has none. */
  to: string | undefined;
}

/** P32 (owner): one read-only mail on Review — who it is for, the address it goes to, and the exact words. */
export function ReviewMailCard({ draft, to }: ReviewMailCardProps) {
  const { message, entry } = draft;
  const name = entry.displayName ?? entry.username;
  const guard = message.addressGuard;
  return (
    <article aria-label={`Email to ${name}`} className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex items-center gap-3 border-b border-border p-3">
        <Avatar size="lg" aria-hidden="true">
          {entry.avatarUrl ? <AvatarImage src={entry.avatarUrl} alt="" /> : null}
          <AvatarFallback>{avatarInitials(name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-base font-medium text-foreground">{name}</p>
          <p className="truncate text-caption-regular text-muted-foreground">@{entry.username}</p>
        </div>
        <p className="ml-auto max-w-[50%] text-right text-base break-all text-foreground">
          {to ? <><span className="sr-only">To </span>{to}</> : <span className="text-muted-foreground"><span className="sr-only">To: </span>No address on file<span className="sr-only"> (won&apos;t be sent)</span></span>}
        </p>
      </header>
      {guard ? (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-body-sm-regular text-amber-900">
          <p role="note">{addressGuardReviewNote(guard.code)}</p>
        </div>
      ) : null}
      {message.subject ? <p className="border-b border-border bg-[#fafaf8] p-3 text-base font-medium text-[#0a0a0a]"><span className="sr-only">Subject: </span>{message.subject}</p> : null}
      {message.bodyRichHtml ? (
        <div className="p-3 text-base text-[#171717]">
          <span className="sr-only">Message: </span>
          <RichBody html={message.bodyRichHtml} />
        </div>
      ) : (
        <p className="p-3 text-base whitespace-pre-wrap text-[#171717]"><span className="sr-only">Message: </span>{message.bodyText}</p>
      )}
    </article>
  );
}
